import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { AnalysisMarkdown, AnalysisSubHeader, BrandIdentity } from '../../../../components';
import type { CreativeLandscapeBrand } from '../../CreativeLandscape.types';
import type { CreativeAxis, CreativeLandscape } from '../../model/creativeLandscapeData.schema';
import { AXIS_EXTENT, axisSideLabel, dotPlacement } from '../../model/positioning';

interface PositioningMapProps {
  creative: CreativeLandscape;
  brands: CreativeLandscapeBrand[];
  /** Print mode: no pointer and no focus, so the detail card is never reachable. */
  exporting?: boolean;
}

/**
 * Graph-paper grid. No utility composes a repeating two-axis gradient, so it is written out
 * here - still off the border token, not a literal colour.
 */
const GRID_STYLE = {
  backgroundImage:
    'linear-gradient(var(--border-light) 1px, transparent 1px), linear-gradient(90deg, var(--border-light) 1px, transparent 1px)',
  backgroundSize: '20% 20%',
} as const;

/**
 * The card is centred in the plot, so it can cover the very dot that opened it. Closing on
 * `mouseleave` alone would then close it the instant it appears, reopen as the dot is exposed
 * again, and flicker. Closing on a short delay lets the pointer cross the gap onto the card,
 * which cancels the close.
 */
const CLOSE_DELAY_MS = 160;

const tickClassName = 'absolute text-[11px] text-muted-foreground';
const neutralChipClassName =
  'absolute rounded border border-border-light bg-card px-1.5 py-px text-[11px] text-muted-foreground';
const captionClassName = 'whitespace-nowrap text-[11px] tracking-[0.02em] text-muted-foreground';

const AxisCaption = ({ axis }: { axis: CreativeAxis }) => {
  const { t } = useTranslation('analysis');
  return (
    <span className={captionClassName}>
      {t('analysisResult.creativeLandscape.axisCaption', {
        low: axis.lowLabel,
        high: axis.highLabel,
      })}
    </span>
  );
};

const AxisScore = ({
  axis,
  value,
  tone,
}: {
  axis: CreativeAxis;
  value: number;
  /** The design separates the two axes by hue; the brand palette avoids both on purpose. */
  tone: 'blue' | 'green';
}) => {
  const text = tone === 'blue' ? 'text-blue-700' : 'text-green-800';
  return (
    <div
      className={`flex flex-col gap-2.5 rounded-[10px] p-3.5 ${
        tone === 'blue' ? 'bg-blue-50 dark:bg-blue-950' : 'bg-green-50 dark:bg-green-950'
      }`}
    >
      <p className={`flex-1 text-pretty text-body-md font-medium ${text} dark:text-foreground`}>
        {axis.lowLabel} / {axis.highLabel}
      </p>
      <span
        className={`text-[32px] font-bold leading-10 ${
          tone === 'blue' ? 'text-blue-800' : 'text-green-900'
        } dark:text-foreground`}
      >
        {value.toFixed(2)}
      </span>
      <span className={`text-body-md font-medium ${text} dark:text-foreground`}>
        {axisSideLabel(value, axis)}
      </span>
    </div>
  );
};

export const PositioningMap = ({ creative, brands, exporting }: PositioningMapProps) => {
  const { t } = useTranslation('analysis');
  const [activeAdvertiser, setActiveAdvertiser] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);
  const open = useCallback(
    (advertiser: string) => {
      cancelClose();
      setActiveAdvertiser(advertiser);
    },
    [cancelClose],
  );
  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setActiveAdvertiser(null), CLOSE_DELAY_MS);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  const { axisX, axisY, brands: positions } = creative.positioning;
  const byName = new Map(brands.map((brand) => [brand.name, brand]));
  const active = activeAdvertiser
    ? (positions.find((p) => p.advertiser === activeAdvertiser) ?? null)
    : null;
  const activeBrand = active ? byName.get(active.advertiser) : undefined;

  return (
    <div className="flex flex-col gap-md">
      <AnalysisSubHeader
        title={t('analysisResult.creativeLandscape.positioningMap')}
        subtitle={t('analysisResult.creativeLandscape.positioningMapSubtitle')}
        subtitleTone="meta"
      />
      <div className="flex flex-wrap items-stretch gap-md rounded-xl border border-border-light bg-card p-md">
        <div className="flex min-w-[360px] flex-[1_1_420px] gap-2.5">
          {/* The vertical axis is captioned vertically, as the design draws it. */}
          <div className="flex shrink-0 items-center justify-center [writing-mode:vertical-rl] [transform:rotate(180deg)]">
            <AxisCaption axis={axisY} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div
              className="relative h-[460px] overflow-hidden rounded-[10px] border border-border-light bg-card"
              style={GRID_STYLE}
            >
              <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
              <div className="absolute inset-y-0 left-1/2 w-px bg-border" />

              <span className={`${neutralChipClassName} left-1/2 top-2 -translate-x-1/2`}>
                {t('analysisResult.creativeLandscape.neutral')}
              </span>
              <span className={`${neutralChipClassName} right-2 top-1/2 -translate-y-1/2`}>
                {t('analysisResult.creativeLandscape.neutral')}
              </span>

              <span className={`${tickClassName} left-2 top-1.5`}>{AXIS_EXTENT}</span>
              <span className={`${tickClassName} bottom-1.5 left-2`}>{-AXIS_EXTENT}</span>
              <span className={`${tickClassName} bottom-1.5 right-2`}>{AXIS_EXTENT}</span>

              {positions.map((position, index) => {
                const placement = dotPlacement(position);
                const brand = byName.get(position.advertiser);
                const isActive = position.advertiser === active?.advertiser;
                return (
                  <button
                    key={position.advertiser}
                    type="button"
                    aria-label={t('analysisResult.creativeLandscape.showBrandAnalysis', {
                      brand: position.advertiser,
                    })}
                    aria-expanded={isActive}
                    disabled={exporting}
                    onMouseEnter={() => open(position.advertiser)}
                    onMouseLeave={closeSoon}
                    onFocus={() => open(position.advertiser)}
                    onBlur={closeSoon}
                    className={clsx(
                      'absolute flex size-9.5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card shadow-sm focus-visible:focus-ring disabled:pointer-events-none',
                      // A ring, not an outline: `focus-ring` owns the outline, and a second
                      // one here would fight it when the dot is both hovered and focused.
                      isActive && 'ring-[3px] ring-purple-600/30',
                    )}
                    style={{
                      left: `${placement.leftPercent}%`,
                      top: `${placement.topPercent}%`,
                      zIndex: isActive ? 7 : 3,
                    }}
                  >
                    <BrandIdentity
                      advertiser={position.advertiser}
                      domain={brand?.domain ?? null}
                      color={brand?.color ?? 'var(--color-blue-600)'}
                    />
                    {/* A print reader cannot hover a dot, so the number ties it to the
                        list of analyses below the map. */}
                    {exporting && (
                      <span className="absolute -top-1.5 -right-1.5 grid size-5 place-items-center rounded-full bg-foreground text-[11px] font-medium text-background">
                        {index + 1}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-center">
              <AxisCaption axis={axisX} />
            </div>
          </div>
        </div>

        {/* The detail reads beside the map rather than over it: a card centred on the plot
            covered the very dots a reader is comparing.

            Nothing here survives export. The dots are disabled in print, so `active` can
            never be set and the panel would show only its idle prompt — telling a reader
            to hover, on paper — while taking a column away from the map. The numbered
            list below carries the same analyses instead. */}
        {!exporting && (
          <aside
            onMouseEnter={cancelClose}
            onMouseLeave={closeSoon}
            className="flex min-w-0 flex-[1_1_240px] flex-col gap-sm"
          >
            {active ? (
              <div data-testid="positioning-detail-card" className="flex flex-col gap-md">
                <div className="flex items-center gap-2.5 rounded-[10px] bg-surface p-sm dark:bg-surface-raised">
                  <BrandIdentity
                    advertiser={active.advertiser}
                    domain={activeBrand?.domain ?? null}
                    color={activeBrand?.color ?? 'var(--color-blue-600)'}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span className="text-body-lg font-medium text-foreground">
                      {active.advertiser}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('analysisResult.creativeLandscape.brandPositioningAnalysis')}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 items-stretch gap-sm">
                  <AxisScore axis={axisX} value={active.x} tone="blue" />
                  <AxisScore axis={axisY} value={active.y} tone="green" />
                </div>
                <AnalysisMarkdown
                  text={active.analysis}
                  className="text-pretty text-body-md text-foreground-alt"
                />
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-[10px] bg-surface p-5 dark:bg-surface-raised">
                <span className="text-pretty text-center text-body-md text-muted-foreground">
                  {t('analysisResult.creativeLandscape.hoverBrandPrompt')}
                </span>
              </div>
            )}
          </aside>
        )}
      </div>
      {exporting && (
        <ul className="flex flex-col gap-md">
          {positions.map((position, index) => (
            <li key={position.advertiser} className="flex flex-col gap-2xs">
              <span className="flex items-center gap-xs">
                <span className="grid size-5 shrink-0 place-items-center rounded-full bg-foreground text-[11px] font-medium text-background">
                  {index + 1}
                </span>
                <span className="text-body-md font-medium text-foreground">
                  {position.advertiser}
                </span>
              </span>
              {/* The scores the hover panel shows on screen, which a print reader would
                  otherwise never see. */}
              <span className="text-body-md text-muted-foreground">
                {axisX.lowLabel} / {axisX.highLabel}: {position.x.toFixed(2)}
                {' · '}
                {axisY.lowLabel} / {axisY.highLabel}: {position.y.toFixed(2)}
              </span>
              <AnalysisMarkdown
                text={position.analysis}
                className="text-body-md text-foreground-alt"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
